import { useState, useEffect } from 'react';
import axios from 'axios';
import { Navigation } from '../components/Navigation';
import { Footer } from '../components/Footer';
import { Button } from '../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Heart, Calendar, User, Filter, ArrowRight } from 'lucide-react';
import { Link } from 'react-router-dom';

const API = process.env.REACT_APP_BACKEND_URL || '';

export const PartnerSearchPage = () => {
  const [searches, setSearches] = useState([]);
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [courseFilter, setCourseFilter] = useState('all');

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [searchesRes, coursesRes] = await Promise.all([
        axios.get(`${API}/api/partner-search.php`),
        axios.get(`${API}/api/courses.php?action=list`)
      ]);
      setSearches(searchesRes.data);
      setCourses(coursesRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const getCourseName = (courseId) => {
    const course = courses.find(c => c.id === courseId);
    return course?.name || 'Unbekannter Kurs';
  };

  const filteredSearches = courseFilter === 'all' 
    ? searches 
    : searches.filter(s => s.course_id === courseFilter);

  return (
    <div className="min-h-screen bg-background">
      <Navigation />
      
      <main className="pt-32 pb-16 px-6" data-testid="partner-search-page">
        <div className="max-w-5xl mx-auto">
          {/* Header */}
          <div className="text-center mb-12 animate-fadeIn">
            <p className="text-xs uppercase tracking-widest text-primary mb-4">
              Community
            </p>
            <h1 className="text-4xl md:text-5xl font-heading font-bold mb-4">
              <span className="text-primary">Tanzpartnerbörse</span>
            </h1>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              Finden Sie den perfekten Tanzpartner für Ihren Kurs. 
              Schauen Sie sich die Gesuche an oder erstellen Sie Ihr eigenes.
            </p>
          </div>

          {/* Filter */}
          <div className="flex items-center gap-4 mb-8 animate-fadeIn" style={{ animationDelay: '0.1s' }}>
            <Filter className="w-4 h-4 text-muted-foreground" />
            <Select value={courseFilter} onValueChange={setCourseFilter}>
              <SelectTrigger className="w-64 bg-secondary border-border" data-testid="course-filter">
                <SelectValue placeholder="Alle Kurse" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Alle Kurse</SelectItem>
                {courses.map(course => (
                  <SelectItem key={course.id} value={course.id}>
                    {course.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Search Results */}
          {loading ? (
            <div className="text-center py-16">
              <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin mx-auto" />
            </div>
          ) : filteredSearches.length === 0 ? (
            <div className="text-center py-16 bg-card border border-border rounded-md animate-fadeIn">
              <Heart className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
              <p className="text-muted-foreground mb-6">
                Noch keine Partner-Gesuche{courseFilter !== 'all' && ' für diesen Kurs'}.
              </p>
              <Link to="/courses">
                <Button className="btn-primary gap-2">
                  Zu den Kursen
                  <ArrowRight className="w-4 h-4" />
                </Button>
              </Link>
            </div>
          ) : (
            <div className="space-y-4">
              {filteredSearches.map((search, index) => (
                <div 
                  key={search.id}
                  className="bg-card border border-primary/30 rounded-md p-6 card-hover animate-fadeIn"
                  style={{ animationDelay: `${0.1 + index * 0.05}s` }}
                  data-testid={`partner-search-${search.id}`}
                >
                  <div className="flex flex-col md:flex-row md:items-start gap-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-3">
                        <span className="partner-badge">
                          <Heart className="w-3 h-3" />
                          Sucht Partner
                        </span>
                        <span className="text-xs text-muted-foreground">
                          {new Date(search.created_at).toLocaleDateString('de-DE')}
                        </span>
                      </div>
                      
                      <div className="flex items-center gap-2 mb-3">
                        <User className="w-4 h-4 text-primary" />
                        <span className="font-medium">{search.user_name}</span>
                      </div>
                      
                      <p className="text-foreground mb-4 leading-relaxed">
                        {search.content}
                      </p>
                      
                      <div className="flex items-center gap-2 text-sm text-muted-foreground">
                        <Calendar className="w-4 h-4" />
                        <Link 
                          to={`/courses/${search.course_id}`}
                          className="text-primary hover:underline"
                        >
                          {getCourseName(search.course_id)}
                        </Link>
                      </div>
                    </div>

                    <Link to={`/courses/${search.course_id}`}>
                      <Button variant="outline" className="gap-2 border-primary text-primary hover:bg-primary/10">
                        Zum Kurs
                        <ArrowRight className="w-4 h-4" />
                      </Button>
                    </Link>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* CTA */}
          <div className="mt-16 text-center bg-card/50 border border-border rounded-md p-12 animate-fadeIn">
            <h3 className="text-2xl font-heading font-semibold mb-4">
              Keinen passenden Partner gefunden?
            </h3>
            <p className="text-muted-foreground mb-6">
              Erstellen Sie Ihr eigenes Gesuch auf der Kursseite Ihrer Wahl.
            </p>
            <Link to="/courses">
              <Button className="btn-primary gap-2">
                Kurse durchsuchen
                <ArrowRight className="w-4 h-4" />
              </Button>
            </Link>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};
